/**
 * Hooks pour la gestion des clients
 *
 * @module CustomerHooks
 * @author Équipe Brydge
 * @since 2.0.0
 */

import {useState, useRef, useCallback, useEffect} from 'react';
import axiosInstance, {endpoints} from 'packages/common/utils/axios';
import {useSnackbar} from 'packages/common/components/snackbar';
import {downloadBlob} from 'packages/common/utils/downloadFile';
import {useSWRWithSync} from 'packages/common/hooks/useSWRWithSync';
import {APP_EVENTS, notifyCustomersListUpdated, notifyReservationsUpdated, notifyCustomerConfigUpdated} from 'packages/common/utils/appEventSync';

/**
 * Récupère tous les clients avec pagination et filtres
 *
 * @param {string} companyId - L'ID de la société
 * @param {Object} params - Paramètres de pagination et filtres
 * @return {Object} Données des clients
 */
export const useGetAllCustomers = (companyId, params = {}) => {
  const {enqueueSnackbar} = useSnackbar();
  const endpointUrl = companyId ? `${endpoints.app.customer.all(companyId)}?${new URLSearchParams(params)}` : null;

  const fetcher = async (url) => {
    try {
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 403) {
        enqueueSnackbar('Vous n\'avez pas les droits pour accéder aux clients', {variant: 'error'});
      } else {
        enqueueSnackbar('Erreur lors de la récupération des clients', {variant: 'error'});
      }
      throw error;
    }
  };

  const {data, error, mutate, isValidating} = useSWRWithSync(endpointUrl, fetcher, {
    eventTypes: APP_EVENTS.CUSTOMERS.LIST_UPDATED,
    companyId,
  });

  return {
    customers: data?.profiles || [],
    metadata: data?.pagination,
    isLoading: !data && !error,
    isValidating,
    isError: !!error,
    mutate,
  };
};

/**
 * Récupère les détails d'un client
 *
 * @param {string} companyId - L'ID de la société
 * @param {string} customerId - L'ID du client
 * @return {Object} Données du client
 */
export const useGetCustomerDetails = (companyId, customerId) => {
  const {enqueueSnackbar} = useSnackbar();
  const endpointUrl = (companyId && customerId) ?
    endpoints.app.customer.details(companyId, customerId) :
    null;

  const fetcher = async (url) => {
    try {
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 403) {
        enqueueSnackbar('Vous n\'avez pas les droits pour accéder à ce client', {variant: 'error'});
      } else {
        enqueueSnackbar('Erreur lors de la récupération des détails du client', {variant: 'error'});
      }
      throw error;
    }
  };

  const {data, error, mutate, isValidating} = useSWRWithSync(endpointUrl, fetcher, {
    eventTypes: APP_EVENTS.CUSTOMERS.LIST_UPDATED,
    companyId,
  });

  return {
    customerDetails: data,
    isLoading: !error && !data,
    isValidating,
    isError: !!error,
    mutate,
  };
};

/**
 * Récupère les fiches clients liées (partageant un email ou téléphone)
 *
 * @param {string} companyId - L'ID de la société
 * @param {string} customerId - L'ID du client
 * @return {Object} Données des clients liés
 */
export const useGetLinkedCustomers = (companyId, customerId) => {
  const endpointUrl = (companyId && customerId) ?
    endpoints.app.customer.linked(companyId, customerId) :
    null;

  const fetcher = async (url) => {
    const response = await axiosInstance.get(url);
    return response.data;
  };

  const {data, error, mutate, isValidating} = useSWRWithSync(endpointUrl, fetcher, {
    eventTypes: APP_EVENTS.CUSTOMERS.LIST_UPDATED,
    companyId,
  });

  return {
    linkedCustomers: data || [],
    isLoading: !error && !data,
    isValidating,
    isError: !!error,
    mutate,
  };
};

/**
 * Exporte le fichier client en CSV avec les filtres du listing
 *
 * Le fichier est généré en flux par l'API puis proposé au téléchargement.
 * Le nom de fichier est construit côté client : l'en-tête Content-Disposition
 * n'est pas exposé par la configuration CORS.
 *
 * @return {Object} Contient exportCustomers, loading, error
 */
export const useExportCustomers = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const {enqueueSnackbar} = useSnackbar();

  const exportCustomers = useCallback(
      async (companyId, filters, filename) => {
        setLoading(true);
        setError(null);
        try {
          const response = await axiosInstance.get(endpoints.app.customer.export(companyId), {
            params: filters,
            responseType: 'blob',
          });
          downloadBlob(new Blob([response.data], {type: 'text/csv;charset=utf-8'}), filename);
          enqueueSnackbar('Export du fichier client téléchargé', {variant: 'success'});
        } catch (err) {
          setError(err);
          const message = err.response?.status === 429 ?
            'Trop d\'exports rapprochés, réessayez dans quelques minutes' :
            'Erreur lors de l\'export des clients';
          enqueueSnackbar(message, {variant: 'error'});
          throw err;
        } finally {
          setLoading(false);
        }
      },
      [enqueueSnackbar],
  );

  return {exportCustomers, loading, error};
};

/**
 * Crée un nouveau client
 * @return {Object} Contient createCustomer, loading, error
 */
export const useCreateCustomer = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const {enqueueSnackbar} = useSnackbar();

  const createCustomer = async (companyId, data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(endpoints.app.customer.create(companyId), data);
      enqueueSnackbar('Client créé avec succès !', {variant: 'success'});
      notifyCustomersListUpdated(companyId, {action: 'create'});
      return response.data;
    } catch (err) {
      setError(err);
      enqueueSnackbar(err.response?.data?.message || 'Erreur lors de la création du client', {variant: 'error'});
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {createCustomer, loading, error};
};

/**
 * Met à jour un client existant
 * @return {Object} Contient updateCustomer, loading, error
 */
export const useUpdateCustomer = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const {enqueueSnackbar} = useSnackbar();

  const updateCustomer = async (companyId, customerId, data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.put(
          endpoints.app.customer.update(companyId, customerId),
          data,
      );
      enqueueSnackbar('Client mis à jour avec succès !', {variant: 'success'});
      notifyCustomersListUpdated(companyId, {action: 'update', customerId});
      // Les réservations exposent une copie live du client via $lookup MongoDB.
      // On invalide leur cache pour refléter la modification immédiatement dans
      // les listes et modales de réservation ouvertes.
      notifyReservationsUpdated(companyId, {action: 'customer_updated', customerId});
      return response.data;
    } catch (err) {
      setError(err);
      const message = err.response?.data?.message || 'Erreur lors de la mise à jour du client';
      enqueueSnackbar(message, {variant: 'error'});
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {updateCustomer, loading, error};
};

/**
 * Hook pour la recherche avancée de clients
 * @param {string} companyId - L'ID de la société
 * @return {Object} Fonctions et états pour la recherche
 */
export const useSearchCustomers = (companyId) => {
  const {enqueueSnackbar} = useSnackbar();
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalResults, setTotalResults] = useState(0);

  const companyIdRef = useRef(companyId);
  useEffect(() => {
    companyIdRef.current = companyId;
  }, [companyId]);

  const removeDuplicates = useCallback((profiles) => {
    if (!profiles || !Array.isArray(profiles)) return [];
    const uniqueProfiles = new Map();
    profiles.forEach((profile) => {
      if (profile && profile._id) {
        uniqueProfiles.set(profile._id, profile);
      }
    });
    return Array.from(uniqueProfiles.values());
  }, []);

  const quickSearch = useCallback(async (term) => {
    if (!term || typeof term !== 'string' || term.trim().length < 2 || !companyIdRef.current) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const cleanTerm = term.trim();
      const params = new URLSearchParams({name: cleanTerm, limit: '30'});
      const response = await axiosInstance.get(
          `${endpoints.app.customer.search(companyIdRef.current)}?${params.toString()}`,
      );
      const uniqueProfiles = removeDuplicates(response.data.profiles || []);
      setSearchResults(uniqueProfiles);
      setTotalResults(uniqueProfiles.length);
    } catch (err) {
      setError(err);
      enqueueSnackbar('Erreur lors de la recherche des clients', {variant: 'error'});
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar, removeDuplicates]);

  const advancedSearch = useCallback(async (searchParams = {}) => {
    if (!companyIdRef.current) return;

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          const strValue = String(value).trim();
          if (strValue) params.append(key, strValue);
        }
      });
      if (!params.has('limit')) params.append('limit', '50');

      const response = await axiosInstance.get(
          `${endpoints.app.customer.search(companyIdRef.current)}?${params.toString()}`,
      );
      const uniqueProfiles = removeDuplicates(response.data.profiles || []);
      setSearchResults(uniqueProfiles);
      setTotalResults(uniqueProfiles.length);
    } catch (err) {
      setError(err);
      enqueueSnackbar('Erreur lors de la recherche avancée', {variant: 'error'});
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar, removeDuplicates]);

  const clearResults = useCallback(() => {
    setSearchResults([]);
    setTotalResults(0);
  }, []);

  return {searchResults, isLoading, error, totalResults, quickSearch, advancedSearch, clearResults};
};

/**
 * Récupère la configuration client de l'établissement (niveaux de fidélité, etc.)
 *
 * Lecture silencieuse : en cas d'erreur on ne montre pas de snackbar (l'affichage
 * du niveau de fidélité retombe sur les valeurs par défaut côté consommateur).
 *
 * @param {string} companyId - L'ID de la société
 * @return {Object} {config, isLoading, isValidating, isError, mutate}
 */
export const useGetCustomerConfig = (companyId) => {
  const endpointUrl = companyId ? endpoints.app.customer.config(companyId) : null;

  const fetcher = async (url) => {
    const response = await axiosInstance.get(url);
    return response.data;
  };

  const {data, error, mutate, isValidating} = useSWRWithSync(endpointUrl, fetcher, {
    eventTypes: APP_EVENTS.CUSTOMERS.CONFIG_UPDATED,
    companyId,
  });

  return {
    config: data,
    isLoading: !error && !data,
    isValidating,
    isError: !!error,
    mutate,
  };
};

/**
 * Met à jour la configuration client de l'établissement.
 *
 * N'affiche pas de snackbar de succès : le retour visuel de sauvegarde est géré
 * par le système d'auto-save des réglages. Affiche une erreur en cas d'échec.
 *
 * @return {Object} {updateConfig, loading, error}
 */
export const useUpdateCustomerConfig = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const {enqueueSnackbar} = useSnackbar();

  const updateConfig = async (companyId, data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.put(endpoints.app.customer.config(companyId), data);
      notifyCustomerConfigUpdated(companyId);
      return response.data;
    } catch (err) {
      setError(err);
      const message = err.response?.data?.message || 'Erreur lors de la mise à jour de la configuration clients';
      enqueueSnackbar(message, {variant: 'error'});
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {updateConfig, loading, error};
};

/**
 * Crée la configuration client. Le backend matérialise le document à la volée
 * via le même endpoint PUT, donc la création délègue à la mise à jour.
 * Présent pour satisfaire le contrat de useSectionConfigForm.
 *
 * @return {Object} {createConfig, loading, error}
 */
export const useCreateCustomerConfig = () => {
  const {updateConfig, loading, error} = useUpdateCustomerConfig();
  return {createConfig: updateConfig, loading, error};
};
